import React from 'react';
import {
  useGetOne,
} from 'react-admin';
import {
  Box,
  Typography,
  Paper,
  Stack,
  Grid,
  Chip,
  Divider,
  Card,
  CardContent,
  IconButton,
  LinearProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  CircularProgress,
  Alert,
} from '@mui/material';
import {
  AccessTime as TimeIcon,
  LocalShipping as DrivingIcon,
  Person as OnDutyIcon,
  Person as PersonIcon,
  CalendarToday as CalendarIcon,
  Email as EmailIcon,
  Schedule as ScheduleIcon,
  CheckCircle as CheckIcon,
  Close as CloseIcon,
} from '@mui/icons-material';

const formatDuration = (seconds: number): { display: string; detailed: string } => {
  const hours = Math.floor(seconds / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);

  const displayParts = [];
  if (hours > 0) displayParts.push(`${hours}h`);
  if (minutes > 0) displayParts.push(`${minutes}m`);
  if (displayParts.length === 0) displayParts.push('0m');

  const detailedParts = [];
  if (hours > 0) detailedParts.push(`${hours} hour${hours !== 1 ? 's' : ''}`);
  if (minutes > 0) detailedParts.push(`${minutes} minute${minutes !== 1 ? 's' : ''}`);
  if (detailedParts.length === 0) detailedParts.push('0 minutes');

  return {
    display: displayParts.join(' '),
    detailed: detailedParts.join(', ')
  };
};

const TimeCard = ({
  icon,
  label,
  value,
  color,
  subtitle,
  percentage
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  color: string;
  subtitle?: string;
  percentage?: number;
}) => (
  <Card sx={{ height: '100%', position: 'relative', overflow: 'visible' }}>
    <CardContent>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Box sx={{
            p: 1,
            borderRadius: 2,
            bgcolor: `${color}.50`,
            display: 'flex',
            alignItems: 'center',
          }}>
            {icon}
          </Box>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
        </Box>

        <Box>
          <Typography variant="h3" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>

        {percentage !== undefined && (
          <Box>
            <LinearProgress
              variant="determinate"
              value={percentage}
              sx={{
                height: 8,
                borderRadius: 4,
                bgcolor: 'grey.200',
                '& .MuiLinearProgress-bar': {
                  bgcolor: color,
                  borderRadius: 4,
                }
              }}
            />
            <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5 }}>
              {percentage}% of total time
            </Typography>
          </Box>
        )}
      </Stack>
    </CardContent>
  </Card>
);

const InfoCard = ({
  icon,
  title,
  items
}: {
  icon: React.ReactNode;
  title: string;
  items: Array<{ label: string; value: React.ReactNode }>;
}) => (
  <Card sx={{ height: '100%' }}>
    <CardContent>
      <Stack spacing={2}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon}
          <Typography variant="h6" fontWeight="medium">
            {title}
          </Typography>
        </Box>
        <Divider />
        <Stack spacing={2}>
          {items.map((item, index) => (
            <Box key={index}>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {item.label}
              </Typography>
              <Typography variant="body1" fontWeight="medium">
                {item.value}
              </Typography>
            </Box>
          ))}
        </Stack>
      </Stack>
    </CardContent>
  </Card>
);

export const DriverTimeLogDialog = ({ open, onClose, timeLogId }: {
  open: boolean;
  onClose: () => void;
  timeLogId: number | null;
}) => {
  const { data: record, isLoading: loading, error } = useGetOne<any>(
    'driver-time-logs',
    { id: timeLogId || 0 },
    { enabled: !!timeLogId }
  );

  if (!open || !timeLogId) return null;

  if (loading) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '300px' }}>
          <CircularProgress />
        </DialogContent>
      </Dialog>
    );
  }

  if (error) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="error">{error.message || 'An error occurred'}</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  if (!record) {
    return (
      <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
        <DialogContent>
          <Alert severity="warning">Time log not found</Alert>
        </DialogContent>
      </Dialog>
    );
  }

  const drivingDuration = formatDuration(record.durationDriving || 0);
  const onDutyDuration = formatDuration(record.durationOnDuty || 0);
  const offDutyDuration = formatDuration(record.durationOffDuty || 0);
  const personalConveyanceDuration = formatDuration(record.durationPersonalConveyance || 0);

  // Shift total = On Duty + Driving (per specs)
  const shiftTotalSeconds = (record.durationDriving || 0) + (record.durationOnDuty || 0);
  const shiftTotalDuration = formatDuration(shiftTotalSeconds);

  // Total time includes all states
  const totalSeconds = shiftTotalSeconds + (record.durationOffDuty || 0) + (record.durationPersonalConveyance || 0);

  const drivingPercentage = totalSeconds > 0
    ? Math.round((record.durationDriving / totalSeconds) * 100)
    : 0;
  const onDutyPercentage = totalSeconds > 0
    ? Math.round((record.durationOnDuty / totalSeconds) * 100)
    : 0;
  const offDutyPercentage = totalSeconds > 0
    ? Math.round(((record.durationOffDuty || 0) / totalSeconds) * 100)
    : 0;
  const personalConveyancePercentage = totalSeconds > 0
    ? Math.round(((record.durationPersonalConveyance || 0) / totalSeconds) * 100)
    : 0;

  const startDate = new Date(record.startedAt);
  const submitDate = new Date(record.submittedAt);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h6">
            Time Log - {startDate.toLocaleDateString()}
          </Typography>
          <IconButton onClick={onClose}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ p: 4 }}>
        <Grid container spacing={4}>
          {/* Shift Total - Prominent */}
          <Grid item xs={12}>
            <Box sx={{
              border: '2px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              p: 3,
              bgcolor: 'primary.50'
            }}>
              <Typography variant="overline" color="text.secondary" fontWeight="bold" letterSpacing={1}>
                SHIFT TOTAL
              </Typography>
              <Typography variant="h2" fontWeight="bold" color="primary.main" sx={{ mt: 1 }}>
                {shiftTotalDuration.display}
              </Typography>
            </Box>
          </Grid>

          {/* Time Breakdown List */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight="medium" color="text.secondary">
                  ON DUTY
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {onDutyDuration.display}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight="medium" color="text.secondary">
                  DRIVING
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {drivingDuration.display}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight="medium" color="text.secondary">
                  OFF DUTY
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {offDutyDuration.display}
                </Typography>
              </Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', py: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}>
                <Typography variant="body1" fontWeight="medium" color="text.secondary">
                  PERSONAL CONVEYANCE
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  {personalConveyanceDuration.display}
                </Typography>
              </Box>
            </Stack>
          </Grid>

          {/* Time & Driver Info */}
          <Grid item xs={12} md={6}>
            <Stack spacing={3}>
              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight="medium">
                  START DATE & TIME
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {startDate.toLocaleString()}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight="medium">
                  SUBMITTED
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {submitDate.toLocaleString()}
                </Typography>
              </Box>

              <Divider />

              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight="medium">
                  DRIVER
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.user?.name || 'Unknown'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight="medium">
                  EMAIL
                </Typography>
                <Typography variant="body1" fontWeight="medium">
                  {record.user?.email || 'N/A'}
                </Typography>
              </Box>

              <Box>
                <Typography variant="overline" color="text.secondary" fontWeight="medium">
                  STATUS
                </Typography>
                <Chip
                  icon={<CheckIcon />}
                  label="Submitted"
                  color="success"
                  size="small"
                  variant="outlined"
                  sx={{ mt: 0.5 }}
                />
              </Box>
            </Stack>
          </Grid>
        </Grid>
      </DialogContent>
    </Dialog>
  );
};